import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { 
  Mail, Phone, User as UserIcon, KeyRound, MapPin, Network, 
  Copy, Check, ShieldAlert, CheckCircle2, Eye, EyeOff 
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { get, post } from "@/lib/api-client";
import { newIdempotencyKey } from "@/lib/idempotency";
import { useAuthStore } from "@/store/auth-store";
import type { RegisterPayload, User as UserT, AuthTokens } from "@/types/auth";

// Extended payload to include sponsor_id
interface ExtendedRegisterPayload extends RegisterPayload {
  sponsor_id?: string;
}

export function RegisterPage() {
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [successUser, setSuccessUser] = useState<UserT | null>(null);
  const [copied, setCopied] = useState(false);
  
  // 🛠️ NEW: Live Search State for Sponsor
  const [sponsorData, setSponsorData] = useState<any | null>(null);
  const [searchingSponsor, setSearchingSponsor] = useState(false);
  const [sponsorConfirmed, setSponsorConfirmed] = useState(false); // Used to lock the input once selected
  
  // 🛠️ ADDED: States for Show/Hide Password
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const setSession = useAuthStore((s) => s.setSession);
  const nav = useNavigate();

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<ExtendedRegisterPayload>();
  const pwd = watch("password");
  const sponsorIdValue = watch("sponsor_id");

  // Auto-fill Sponsor ID from URL (?ref=USR000123)
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setValue("sponsor_id", ref.toUpperCase());
  }, [searchParams, setValue]);

  // 🛠️ NEW: Live Search for Sponsor
  useEffect(() => {
    const cleanId = sponsorIdValue ? sponsorIdValue.trim().toUpperCase() : "";
    if (!cleanId || cleanId.length < 5 || sponsorConfirmed) {
      setSponsorData(null);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingSponsor(true);
      try {
        const res = await get<any>(`/public/users/search?user_id=${cleanId}`);
        setSponsorData(res);
      } catch {
        setSponsorData(null);
      } finally {
        setSearchingSponsor(false);
      }
    }, 500); 
    return () => clearTimeout(timer);
  }, [sponsorIdValue, sponsorConfirmed]);

  const handleConfirmSponsor = () => {
    setSponsorConfirmed(true);
  };

  const handleClearSponsor = () => {
    setSponsorConfirmed(false);
    setSponsorData(null);
    setValue("sponsor_id", "");
  };

  const onSubmit = async (data: ExtendedRegisterPayload) => {
    setLoading(true);
    try {
      // 🛠️ FIXED: Extracting the actual structure returned by api-client.ts post method
      const res = await post<any>(
        "/auth/register", 
        {
          ...data,
          sponsor_id: data.sponsor_id ? data.sponsor_id.trim().toUpperCase() : "" // Ensure backend gets clean data
        }, 
        { idempotencyKey: newIdempotencyKey() }
      );
      
      // Because your custom post() returns r.data.data
      const userObj = res.user;
      const tokens: AuthTokens = { access: res.access, refresh: res.refresh };
      
      setSession(userObj, tokens);
      setSuccessUser(userObj);
      
    } catch (err: any) { 
      toast.error(err.message || "Registration failed. Please check your details.");
    } finally { 
      setLoading(false); 
    }
  };

  const handleCopyId = async () => {
    if (!successUser) return;
    await navigator.clipboard.writeText(successUser.user_id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const finishLogin = () => {
    setSuccessUser(null);
    toast.success("Welcome to Enterprise Auto Club!");
    nav("/", { replace: true });
  };

  return (
    <div className="animate-fade-in">
      <h1 className="text-2xl font-bold text-surface-900">Create your account</h1>
      <p className="text-sm text-surface-500 mt-1.5">
        Registration is free. You will remain an Inactive Member until you join a Club.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-4">
        
        {/* 🛠️ SPONSOR ID BLOCK */}
        <div className="space-y-2 mb-6">
          <Input
            label="Sponsor ID (Optional)" 
            placeholder="e.g. MS000123"
            leftIcon={<Network className="h-4 w-4" />}
            hint={!sponsorConfirmed ? "Leave blank if you don't have a referral." : undefined}
            disabled={sponsorConfirmed} // Lock input once confirmed
            {...register("sponsor_id")}
            onChange={(e) => setValue("sponsor_id", e.target.value.toUpperCase())}
          />

          {/* Live Preview Box */}
          {!sponsorConfirmed && sponsorIdValue && sponsorIdValue.length >= 5 && (
            <div className="min-h-[50px] animate-fade-in">
              {searchingSponsor ? (
                <div className="text-sm text-surface-500 p-2">Searching sponsor...</div>
              ) : sponsorData ? (
                <div className="flex items-center justify-between p-3 rounded-xl border border-success-200 bg-success-50/50 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-success-100 flex items-center justify-center text-success-700">
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-success-900 leading-tight">
                        {sponsorData.full_name}
                      </div>
                      <div className="text-[10px] text-success-700 font-medium">Valid Sponsor Found</div>
                    </div>
                  </div>
                  <Button type="button" size="sm" onClick={handleConfirmSponsor} className="bg-success-600 hover:bg-success-700 text-white">
                    Select
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 rounded-xl border border-danger-200 bg-danger-50 shadow-sm">
                  <ShieldAlert className="h-5 w-5 text-danger-600 shrink-0" />
                  <div className="text-sm text-danger-900 font-medium">User not found. Check the ID again.</div>
                </div>
              )}
            </div>
          )}

          {/* Confirmed Sponsor Box */}
          {sponsorConfirmed && sponsorData && (
            <div className="flex items-center justify-between p-3 rounded-xl border-2 border-brand-200 bg-brand-50 shadow-sm animate-fade-in">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center text-white font-bold shadow-sm">
                  {sponsorData.full_name.charAt(0)}
                </div>
                <div>
                  <div className="text-[10px] uppercase font-bold text-brand-500 tracking-wider">Your Sponsor</div>
                  <div className="text-sm font-bold text-brand-900 leading-tight">
                    {sponsorData.full_name}
                  </div>
                </div>
              </div>
              <button 
                type="button" 
                onClick={handleClearSponsor}
                className="text-xs font-semibold text-brand-600 hover:text-danger-600 transition-colors"
              >
                Change
              </button>
            </div>
          )}
        </div>

        <Input
          label="Full Name" placeholder="Jane Doe"
          leftIcon={<UserIcon className="h-4 w-4" />}
          error={errors.full_name?.message}
          {...register("full_name", { required: "Full name is required", minLength: { value: 2, message: "Too short" } })}
        />
        <Input
          label="Mobile Number" placeholder="+88017000-0000"
          leftIcon={<Phone className="h-4 w-4" />}
          error={errors.mobile?.message}
          {...register("mobile", { required: "Mobile is required" })}
        />
        <Input
          label="Email (optional)" type="email" placeholder="jane@example.com"
          leftIcon={<Mail className="h-4 w-4" />}
          error={errors.email?.message}
          {...register("email")}
        />
        <Input
          label="Address (optional)" placeholder="Street, City, ZIP"
          leftIcon={<MapPin className="h-4 w-4" />}
          {...register("address")}
        />
        
        {/* 🛠️ ADDED: Show/Hide Toggle for Password */}
        <Input
          label="Password" 
          type={showPassword ? "text" : "password"} 
          placeholder="6 to 10 characters"
          leftIcon={<KeyRound className="h-4 w-4" />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-surface-400 hover:text-surface-600 focus:outline-none"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          }
          hint="Any 6 to 10 characters."
          error={errors.password?.message}
          {...register("password", {
            required: "Password is required",
            minLength: { value: 6, message: "Minimum 6 characters" },
            maxLength: { value: 10, message: "Maximum 10 characters" },
          })}
        />

        {/* 🛠️ ADDED: Show/Hide Toggle for Confirm Password */}
        <Input
          label="Confirm Password" 
          type={showConfirmPassword ? "text" : "password"} 
          placeholder="Retype password"
          leftIcon={<KeyRound className="h-4 w-4" />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="text-surface-400 hover:text-surface-600 focus:outline-none"
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          }
          error={errors.password_confirm?.message}
          {...register("password_confirm", {
            required: "Please confirm your password",
            validate: (v) => v === pwd || "Passwords do not match",
          })}
        />
        
        <div className="pt-2">
          <Button type="submit" size="lg" fullWidth loading={loading}>Create account</Button>
        </div>
      </form>

      <div className="mt-6 text-center text-sm text-surface-500">
        Already have an account?{" "}
        <Link to="/login" className="text-brand-600 hover:text-brand-700 font-medium">Sign in</Link>
      </div>

      {/* SUCCESS POPUP */}
      <Modal open={!!successUser} onClose={finishLogin} title="অভিনন্দন! অ্যাকাউন্ট তৈরি হয়েছে" size="md">
        <div className="space-y-6 text-center">
          <div className="text-sm text-surface-700">
            আপনার অ্যাকাউন্টটি সফলভাবে তৈরি হয়েছে এবং আপনাকে স্বয়ংক্রিয়ভাবে লগিন করানো হয়েছে।
          </div>

          <div className="bg-brand-50 border border-brand-200 rounded-xl p-6 relative overflow-hidden">
            <div className="text-xs font-bold uppercase tracking-widest text-brand-700 mb-2">
              আপনার User ID (লগিন করার জন্য)
            </div>
            <div className="text-4xl font-black font-mono text-brand-900 tracking-wider">
              {successUser?.user_id}
            </div>
          </div>

          <div className="bg-warning-50 border border-warning-200 p-4 rounded-lg text-sm text-warning-800 text-left">
            <strong>গুরুত্বপূর্ণ:</strong> অনুগ্রহ করে আপনার <b>User ID</b> এবং <b>Password</b> সযত্নে সেভ করে রাখুন। পরবর্তীতে অ্যাকাউন্টে প্রবেশ করার জন্য এটি প্রয়োজন হবে।
          </div>

          <div className="grid grid-cols-2 gap-3 pt-4">
            <Button variant="outline" size="lg" onClick={handleCopyId} 
                    leftIcon={copied ? <Check className="h-4 w-4 text-success-600" /> : <Copy className="h-4 w-4" />}>
              {copied ? "Copied!" : "Copy User ID"}
            </Button>
            <Button size="lg" onClick={finishLogin}>
              ড্যাশবোর্ডে প্রবেশ করুন
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  );
}